import { useCallback, useEffect, useId, useState } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import Notice from '../../components/Notice.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import { api } from '../../lib/api.js';

// Notifications > Monitored accounts (approved by Hugo Andrade 2026-10-25): break-glass (emergency access) and other
// sensitive accounts the customer admin confirms into a list, each with a type. onGuard Watch raises an alert when one
// of them signs in. onGuard also proposes break-glass candidates (suggestions) with its reasons; the admin confirms one
// with a type or dismisses it. The server decides who may read (403 for plain IPC Global operators) and who may change
// the list (`canEdit`); this page only follows those answers.

const BREAK_GLASS = 'break-glass';
const VENDOR = 'vendor';
const PAGE_SIZE = 10;
const RECOMMENDED_BREAK_GLASS = 2;
// What the last sign-in log read of the collector says when it could not watch these accounts (health.signInRead).
const READ_PROBLEMS = Object.freeze({
  unlicensed: 'Microsoft only keeps sign-in logs for tenants with Microsoft Entra ID P1 or P2. Without that license no sign-in alert can be raised; the list is kept and starts working once the license is in place.',
  denied: 'The onGuard application in your tenant is missing the permission to read sign-in logs (AuditLog.Read.All and Directory.Read.All). Ask your Microsoft Entra administrator to grant admin consent again.',
  failed: 'The last read of the sign-in logs failed. Watch tries again on its next run, within 15 minutes; tell your onGuard administrator if this stays.',
});

// The fallback when the server sends no `types` (an older portal API). The server's list wins when present.
const DEFAULT_TYPES = Object.freeze([
  { id: BREAK_GLASS, label: 'Break-glass (emergency access)' },
  { id: 'service-account', label: 'Service account' },
  { id: VENDOR, label: 'Vendor or temporary' },
  { id: 'former-employee', label: 'Former employee or disabled' },
  { id: 'vip', label: 'Executive / VIP' },
]);

const accountName = (a) => a.displayName || a.userPrincipalName || a.directoryUserId || 'Unknown account';

function formatDate(iso) {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function windowText(a) {
  if (a.type !== VENDOR) return 'Not applicable';
  if (!a.windowStart && !a.windowEnd) return 'Any time';
  if (!a.windowStart) return `Until ${a.windowEnd}`;
  if (!a.windowEnd) return `From ${a.windowStart}`;
  return `${a.windowStart} to ${a.windowEnd}`;
}

// A window is optional; when both dates are given the end may not come before the start.
// A vendor window needs both dates (the server also caps the end at 366 days from today).
function windowProblem(start, end) {
  if (!start || !end) return 'A vendor or temporary account needs both window dates.';
  if (end < start) return 'The window end date cannot be before its start date.';
  return null;
}

// `exclude`: a type this select cannot switch to (a row cannot become a vendor account: that needs window dates, so the
// account is removed and added again as a vendor).
function TypeSelect({ id, value, types, onChange, label, disabled = false, exclude = null }) {
  return (
    <select id={id} className="input py-1.5" aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {types.filter((t) => t.id === value || t.id !== exclude).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
    </select>
  );
}

function WindowFields({ start, end, onStart, onEnd }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block text-sm">
        <span className="label">Allowed from</span>
        <input type="date" className="input" required value={start} onChange={(e) => onStart(e.target.value)} />
      </label>
      <label className="block text-sm">
        <span className="label">Allowed until</span>
        <input type="date" className="input" required value={end} onChange={(e) => onEnd(e.target.value)} />
      </label>
    </div>
  );
}

function SuggestionCard({ suggestion, types, busy, onConfirm, onDismiss }) {
  const [type, setType] = useState(BREAK_GLASS);
  const selectId = useId();
  const name = accountName(suggestion);
  const reasons = Array.isArray(suggestion.reasonText) ? suggestion.reasonText : [];
  return (
    <li className="border border-ipc-border bg-white p-4" data-testid="monitored-suggestion">
      <p className="font-medium text-ipc-ink">{name}</p>
      {suggestion.userPrincipalName && suggestion.userPrincipalName !== name ? <p className="break-all text-xs text-ipc-muted">{suggestion.userPrincipalName}</p> : null}
      {reasons.length > 0 ? (
        <div className="mt-3 text-sm">
          <p className="text-ipc-muted">Why suggested</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ipc-ink">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[14rem] flex-1">
          <label htmlFor={selectId} className="label">Type</label>
          <TypeSelect id={selectId} value={type} types={types} onChange={setType} disabled={busy} />
        </div>
        <button type="button" className="btn-primary" disabled={busy} onClick={() => onConfirm(suggestion, type)} aria-label={`Confirm ${name}`}>Confirm</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => onDismiss(suggestion)} aria-label={`Dismiss ${name}`}>Dismiss</button>
      </div>
    </li>
  );
}

function AddAccountForm({ types, busy, full, max, onAdd }) {
  const [upn, setUpn] = useState('');
  const [type, setType] = useState(BREAK_GLASS);
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [problem, setProblem] = useState(null);
  const upnId = useId();
  const typeId = useId();

  async function submit(e) {
    e.preventDefault();
    if (busy || full) return;
    const name = upn.trim();
    if (!name) { setProblem('Enter the account\'s user principal name.'); return; }
    const body = { type, userPrincipalName: name };
    if (type === VENDOR) {
      const p = windowProblem(windowStart, windowEnd);
      if (p) { setProblem(p); return; }
      if (windowStart) body.windowStart = windowStart;
      if (windowEnd) body.windowEnd = windowEnd;
    }
    setProblem(null);
    const ok = await onAdd(body);
    if (ok) { setUpn(''); setType(BREAK_GLASS); setWindowStart(''); setWindowEnd(''); }
  }

  return (
    <SectionCard title="Add an account" description="Any account in your directory, by its user principal name.">
      {full ? <p className="mb-3 text-sm text-ipc-muted">The list holds at most {max} accounts. Remove one before adding another.</p> : null}
      <form className="space-y-3" onSubmit={submit} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={upnId} className="label">User principal name</label>
            <input id={upnId} type="text" className="input" autoComplete="off" spellCheck={false} placeholder="name@customer-domain.com" value={upn} onChange={(e) => setUpn(e.target.value)} disabled={full} />
          </div>
          <div>
            <label htmlFor={typeId} className="label">Type</label>
            <TypeSelect id={typeId} value={type} types={types} onChange={setType} disabled={full} />
          </div>
        </div>
        {type === VENDOR ? <WindowFields start={windowStart} end={windowEnd} onStart={setWindowStart} onEnd={setWindowEnd} /> : null}
        {type === VENDOR ? <p className="text-xs text-ipc-muted">A sign-in outside the allowed window raises an alert. Leave both dates empty to alert on every sign-in.</p> : null}
        <DialogError message={problem} />
        <button type="submit" className="btn-primary" disabled={busy || full} aria-busy={busy}>Add account</button>
      </form>
    </SectionCard>
  );
}

export default function MonitoredAccounts({ customerId }) {
  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState(null); // { status, message }
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: 'ok'|'error', text }
  const [removing, setRemoving] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setView(await api.listWatchMonitored(customerId));
    } catch (e) {
      setLoadError({ status: e?.status ?? null, message: e?.message || 'Monitored accounts could not be loaded. Try again in a few minutes.' });
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // A refresh after a change: a failure is a page notice, the change itself already happened.
  async function refresh() {
    try {
      setView(await api.listWatchMonitored(customerId));
    } catch (e) {
      setNotice({ tone: 'error', text: e?.message || 'Saved, but the list could not be refreshed. Reload to try again.' });
    }
  }

  // Runs one change; returns true when it succeeded.
  async function change(fn, okText) {
    if (busy) return false;
    setBusy(true);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setNotice({ tone: 'error', text: e?.message || 'That did not work. Try again.' });
      setBusy(false);
      return false;
    }
    setNotice({ tone: 'ok', text: okText });
    setBusy(false);
    await refresh();
    return true;
  }

  if (loadError) {
    if (loadError.status === 403) {
      return <PageState state="error" title="Monitored accounts are not available to you" message={loadError.message} />;
    }
    return <PageState state="error" title="Monitored accounts could not be loaded" message={loadError.message} onRetry={load} />;
  }
  if (!view) return <PageState state="loading" header={false} cards={0} label="Loading monitored accounts" />;

  const accounts = Array.isArray(view.accounts) ? view.accounts : [];
  const suggestions = Array.isArray(view.suggestions) ? view.suggestions : [];
  const types = Array.isArray(view.types) && view.types.length > 0 ? view.types : DEFAULT_TYPES;
  const typeLabel = (id) => types.find((t) => t.id === id)?.label ?? id;
  const canEdit = view.canEdit === true;
  const max = Number.isInteger(view.max) ? view.max : 50;
  const full = accounts.length >= max;
  const breakGlassCount = accounts.filter((a) => a.type === BREAK_GLASS).length;

  const confirmSuggestion = (s, type) => change(
    () => api.addWatchMonitored(customerId, { type, directoryUserId: s.directoryUserId, ...(s.userPrincipalName ? { userPrincipalName: s.userPrincipalName } : {}) }),
    `${accountName(s)} is now monitored as ${typeLabel(type)}.`,
  );
  const dismissSuggestion = (s) => change(
    () => api.dismissWatchMonitoredSuggestion(customerId, s.directoryUserId),
    `${accountName(s)} is no longer suggested.`,
  );
  const addAccount = (body) => change(
    () => api.addWatchMonitored(customerId, body),
    `${body.userPrincipalName} is now monitored as ${typeLabel(body.type)}.`,
  );
  const changeType = (a, type) => {
    if (type === a.type) return;
    const body = { type };
    if (type === VENDOR) {
      if (a.windowStart) body.windowStart = a.windowStart;
      if (a.windowEnd) body.windowEnd = a.windowEnd;
    }
    change(() => api.updateWatchMonitored(customerId, a.monitorId, body), `${accountName(a)} is now ${typeLabel(type)}.`);
  };

  async function confirmRemove() {
    if (busy || !removing) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await api.removeWatchMonitored(customerId, removing.monitorId);
    } catch (e) {
      setRemoveError(e?.message || 'Could not remove the account. Try again.');
      setBusy(false);
      return;
    }
    setNotice({ tone: 'ok', text: `${accountName(removing)} is no longer monitored.` });
    setRemoving(null);
    setBusy(false);
    await refresh();
  }

  const columns = [
    {
      id: 'account',
      header: 'Account',
      sortValue: (a) => accountName(a).toLowerCase(),
      cell: (a) => (
        <div className="min-w-0 break-words">
          <div className="font-medium text-ipc-ink">{accountName(a)}</div>
          {a.userPrincipalName && a.userPrincipalName !== accountName(a) ? <div className="text-xs text-ipc-muted">{a.userPrincipalName}</div> : null}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      sortValue: (a) => typeLabel(a.type),
      cell: (a) => (canEdit
        ? <TypeSelect value={a.type} types={types} label={`Type for ${accountName(a)}`} disabled={busy} exclude={VENDOR} onChange={(t) => changeType(a, t)} />
        : typeLabel(a.type)),
    },
    { id: 'window', header: 'Allowed window', hideBelow: 'md', cell: windowText },
    {
      id: 'added',
      header: 'Added',
      hideBelow: 'sm',
      sortValue: (a) => a.addedAt ?? '',
      cell: (a) => (
        <div>
          <div>{formatDate(a.addedAt)}</div>
          {a.addedBy ? <div className="break-all text-xs text-ipc-muted">by {a.addedBy}</div> : null}
        </div>
      ),
    },
  ];

  const rowActions = canEdit
    ? (a) => [{ id: 'remove', label: 'Remove', onSelect: () => { setNotice(null); setRemoveError(null); setRemoving(a); } }]
    : undefined;

  return (
    <div className="space-y-4" data-testid="monitored-accounts">
      <p className="max-w-3xl text-sm text-ipc-muted">
        onGuard Watch raises an alert when a monitored account signs in, and the alert goes to your guardians. A sign-in by a
        break-glass (emergency access) account is Critical and goes to all guardians at once.
      </p>
      {canEdit ? null : <p className="text-sm text-ipc-muted" data-testid="monitored-read-only">Only the customer&apos;s onGuard administrators can change this list.</p>}
      {notice ? (
        notice.tone === 'error'
          ? <Notice tone="bad" titleAs="p" title="That did not work">{notice.text}</Notice>
          : <Notice tone="good" role="status" titleAs="p" title="Saved">{notice.text}</Notice>
      ) : null}
      {breakGlassCount < RECOMMENDED_BREAK_GLASS ? (
        <Notice tone="info" titleAs="p" title="Microsoft recommends at least two break-glass accounts" testId="break-glass-advice">
          {breakGlassCount === 0 ? 'No break-glass account is confirmed yet.' : 'One break-glass account is confirmed.'} Keep at least two emergency
          access accounts so one lost credential never locks you out, and confirm them here so every sign-in is seen.
        </Notice>
      ) : null}
      {READ_PROBLEMS[view.signInRead?.status] ? (
        <Notice tone="warn" titleAs="p" title="Watch cannot read the sign-ins of these accounts" testId="sign-in-read-problem">
          {READ_PROBLEMS[view.signInRead.status]}
        </Notice>
      ) : null}
      {canEdit && suggestions.length > 0 ? (
        <SectionCard
          title="Suggested accounts"
          description={view.suggestionsAt ? `Accounts that look like break-glass accounts, as of ${formatDate(view.suggestionsAt)}.` : 'Accounts that look like break-glass accounts.'}
        >
          <ul className="grid gap-3 lg:grid-cols-2">
            {suggestions.map((s) => (
              <SuggestionCard key={s.directoryUserId} suggestion={s} types={types} busy={busy || full} onConfirm={confirmSuggestion} onDismiss={dismissSuggestion} />
            ))}
          </ul>
        </SectionCard>
      ) : null}
      {canEdit ? <AddAccountForm types={types} busy={busy} full={full} max={max} onAdd={addAccount} /> : null}
      <ConsoleTable
        label="Monitored accounts"
        columns={columns}
        rows={accounts}
        getRowId={(a) => a.monitorId}
        rowName={accountName}
        pageSize={PAGE_SIZE}
        rowActions={rowActions}
        caption={`${accounts.length} of at most ${max} accounts.`}
        emptyState={{
          title: 'No monitored accounts yet',
          body: canEdit ? 'Confirm a suggestion or add an account above to have its sign-ins watched.' : 'An onGuard administrator of this customer can add accounts to watch.',
        }}
      />
      {removing ? (
        <Dialog
          open
          title="Remove monitored account"
          onClose={() => { if (!busy) setRemoving(null); }}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={() => setRemoving(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn-danger" onClick={confirmRemove} disabled={busy} aria-busy={busy}>{`Remove ${accountName(removing)}`}</button>
            </>
          )}
        >
          <p>Sign-ins by <span className="break-all">{accountName(removing)}</span> will no longer raise a monitored account alert.</p>
          <DialogError message={removeError} />
        </Dialog>
      ) : null}
    </div>
  );
}
